import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'
import { useProaStore } from '@/stores/proa-store'
import { get, post } from 'aws-amplify/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { trpc } from '@/api/trpc'
import { useMemo } from 'react'

// ============================================================================
// TYPES
// ============================================================================

export type MonthlyDistributionType = 'even' | 'weighted' | 'front_loaded' | 'back_loaded' | 'custom'
export type EntryMode = 'direct' | 'breakdown'

export interface MonthlyDistribution {
  type: MonthlyDistributionType
  weights?: number[]
  monthlyValues?: number[]
}

export interface ProaScenario {
  scenarioId: string
  name: string
  description?: string
  year: number
  isOfficial: boolean
  isLocked: boolean
  baseScenarioId?: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface MetricBreakdown {
  dimensionType: 'client'
  dimensionId: string
  dimensionLabel: string
  annual: number
  distribution: MonthlyDistribution
  notes?: string
}

export interface MetricProa {
  lineItem: string
  scenarioId: string
  year: number
  entryMode: EntryMode
  annual: number
  breakdowns?: MetricBreakdown[]
  ajuste: number
  distribution: MonthlyDistribution
  notes?: string
  updatedBy: string
  updatedAt: string
}

export interface BreakdownComparisonItem {
  dimensionId: string
  dimensionLabel: string
  proaAnnual: number
  proaYTD: number
  proaMonth: number
  realAnnual: number | null
  realYTD: number | null
  realMonth: number | null
  varianceAnnual: number | null
  varianceYTD: number | null
  varianceMonth: number | null
  achievementPercent: number | null
}

export interface ProaComparisonItem {
  id: string
  name: string
  entryMode: EntryMode
  proaAnnual: number
  proaYTD: number
  proaMonth: number
  ajuste: number
  realAnnual: number | null
  realYTD: number | null
  realMonth: number | null
  varianceAnnual: number | null
  varianceYTD: number | null
  varianceMonth: number | null
  achievementPercent: number | null
  breakdowns?: BreakdownComparisonItem[]
  // FR9, FR10, FR11: Hierarchical DRE grouping support
  parentId?: string | null
  isGrouper?: boolean
  level?: number
  children?: ProaComparisonItem[]
}

// DRE line hierarchy configuration (FR9 - matching actual DRE page structure)
export const DRE_LINE_HIERARCHY: Record<string, { parentId: string | null; isGrouper: boolean; order: number }> = {
  // 1. RECEITA group
  'Receita': { parentId: null, isGrouper: true, order: 1 },
  'FaturamentoTotal': { parentId: 'Receita', isGrouper: false, order: 2 },
  'Repasse': { parentId: 'Receita', isGrouper: false, order: 3 },
  'ReceitaBruta': { parentId: 'Receita', isGrouper: false, order: 4 },
  'ReceitaFluxoGerencial': { parentId: 'Receita', isGrouper: false, order: 6 },

  // 2. IMPOSTOS SOBRE FATURAMENTO group
  'ImpostosFaturamento': { parentId: null, isGrouper: true, order: 10 },
  'PIS': { parentId: 'ImpostosFaturamento', isGrouper: false, order: 11 },
  'COFINS': { parentId: 'ImpostosFaturamento', isGrouper: false, order: 12 },
  'ISS': { parentId: 'ImpostosFaturamento', isGrouper: false, order: 13 },
  'ReceitaLiquida': { parentId: null, isGrouper: false, order: 14 },

  // 3. CUSTOS group
  'Custos': { parentId: null, isGrouper: true, order: 20 },
  'ServicosPrestados': { parentId: 'Custos', isGrouper: false, order: 21 },
  'Timesheet': { parentId: 'Custos', isGrouper: false, order: 22 },
  'Folha': { parentId: 'Custos', isGrouper: false, order: 23 },
  'MaoDeObra': { parentId: 'Custos', isGrouper: false, order: 24 },
  'Freelancer': { parentId: 'Custos', isGrouper: false, order: 25 },
  'FreelancerFixo': { parentId: 'Custos', isGrouper: false, order: 26 },
  'FreelancerEmpresa': { parentId: 'Custos', isGrouper: false, order: 27 },
  'Bonificacao': { parentId: 'Custos', isGrouper: false, order: 28 },
  'LucroBruto': { parentId: null, isGrouper: false, order: 29 },

  // 4. DESPESAS OPERACIONAIS group
  'DespesasOp': { parentId: null, isGrouper: true, order: 30 },
  'DespesasOperacionais': { parentId: 'DespesasOp', isGrouper: false, order: 31 },

  // 5. RESULTADO OPERACIONAL group
  'ResultadoOperacional': { parentId: null, isGrouper: true, order: 40 },
  'ReceitasFinanceiras': { parentId: 'ResultadoOperacional', isGrouper: false, order: 41 },
  'DespesasFinanceiras': { parentId: 'ResultadoOperacional', isGrouper: false, order: 42 },
  'LucroOperacional': { parentId: null, isGrouper: false, order: 43 },

  // 6. IMPOSTOS SOBRE LUCRO group
  'ImpostosLucro': { parentId: null, isGrouper: true, order: 50 },
  'IRPJ': { parentId: 'ImpostosLucro', isGrouper: false, order: 51 },
  'ContribuicaoSocial': { parentId: 'ImpostosLucro', isGrouper: false, order: 52 },
  'LucroLiquido': { parentId: null, isGrouper: false, order: 53 },
  // Derived stretch targets for Lucro Líquido
  'LucroLiquido120': { parentId: null, isGrouper: false, order: 54 },
  'LucroLiquido150': { parentId: null, isGrouper: false, order: 55 },
}

// Grouper display names (matching DRE page format)
const GROUPER_NAMES: Record<string, string> = {
  'Receita': '1. RECEITA',
  'ImpostosFaturamento': '2. IMPOSTOS',
  'Custos': '3. CUSTOS',
  'DespesasOp': '4. DESPESAS OPERACIONAIS',
  'ResultadoOperacional': '5. RESULTADO OPERACIONAL',
  'ImpostosLucro': '6. IMPOSTOS SOBRE LUCRO',
}

// Line item display names (matching DRE page labels)
const LINE_ITEM_NAMES: Record<string, string> = {
  'FaturamentoTotal': 'Faturamento Bruto',
  'Repasse': 'Repasse / Simples Remessa',
  'ReceitaBruta': 'Receita Bruta',
  'ReceitaFluxoGerencial': 'Receita Fluxo Gerencial',
  'PIS': 'PIS',
  'COFINS': 'COFINS',
  'ISS': 'ISS',
  'ReceitaLiquida': 'Receita Líquida',
  'ServicosPrestados': 'Serviços Prestados (Terceiros)',
  'Timesheet': 'Folha Timesheet',
  'Folha': 'Folha Comum',
  'MaoDeObra': 'Mão de Obra',
  'Freelancer': 'Freelancer',
  'FreelancerFixo': 'Freelancer Fixo',
  'FreelancerEmpresa': 'Freelancer Empresa',
  'Bonificacao': 'Bonificação',
  'LucroBruto': 'Lucro Bruto',
  'DespesasOperacionais': 'Despesas Operacionais',
  'ReceitasFinanceiras': 'Receitas Financeiras',
  'DespesasFinanceiras': 'Despesas Financeiras',
  'LucroOperacional': 'Lucro Operacional',
  'IRPJ': 'IRPJ',
  'ContribuicaoSocial': 'Contribuição Social',
  'LucroLiquido': 'Lucro Líquido',
  'LucroLiquido120': 'Lucro Líquido 120%',
  'LucroLiquido150': 'Lucro Líquido 150%',
}

// Helper to add hierarchy info to metrics and create grouper rows
export function enrichMetricsWithHierarchy(metrics: ProaComparisonItem[]): ProaComparisonItem[] {
  // Create a map for quick lookup
  const metricsMap = new Map(metrics.map(m => [m.id, m]))

  // Find all unique grouper IDs that have children in the data
  const grouperIds = new Set<string>()
  metrics.forEach(metric => {
    const hierarchyInfo = DRE_LINE_HIERARCHY[metric.id]
    if (hierarchyInfo?.parentId) {
      grouperIds.add(hierarchyInfo.parentId)
    }
  })

  // Create grouper rows with aggregated totals
  const grouperRows: ProaComparisonItem[] = []

  grouperIds.forEach(grouperId => {
    const hierarchyInfo = DRE_LINE_HIERARCHY[grouperId]
    if (!hierarchyInfo) return

    // Find all children metrics for this grouper
    const children = metrics.filter(m => {
      const childInfo = DRE_LINE_HIERARCHY[m.id]
      return childInfo?.parentId === grouperId
    })

    // Aggregate totals from children
    const proaAnnual = children.reduce((sum, c) => sum + (c.proaAnnual || 0), 0)
    const proaYTD = children.reduce((sum, c) => sum + (c.proaYTD || 0), 0)
    const proaMonth = children.reduce((sum, c) => sum + (c.proaMonth || 0), 0)
    const realYTD = children.some(c => c.realYTD !== null)
      ? children.reduce((sum, c) => sum + (c.realYTD || 0), 0)
      : null
    const realMonth = children.some(c => c.realMonth !== null)
      ? children.reduce((sum, c) => sum + (c.realMonth || 0), 0)
      : null
    const realAnnual = children.some(c => c.realAnnual !== null)
      ? children.reduce((sum, c) => sum + (c.realAnnual || 0), 0)
      : null

    grouperRows.push({
      id: grouperId,
      name: GROUPER_NAMES[grouperId] || grouperId,
      entryMode: 'direct' as EntryMode,
      proaAnnual,
      proaYTD,
      proaMonth,
      ajuste: 0,
      realAnnual,
      realYTD,
      realMonth,
      varianceAnnual: realAnnual !== null ? realAnnual - proaAnnual : null,
      varianceYTD: realYTD !== null ? realYTD - proaYTD : null,
      varianceMonth: realMonth !== null ? realMonth - proaMonth : null,
      achievementPercent: realYTD !== null && proaYTD !== 0 ? (realYTD / proaYTD) * 100 : null,
      parentId: hierarchyInfo.parentId ?? null,
      isGrouper: true,
      level: 0,
    })
  })

  // Add hierarchy info to each existing metric
  const enrichedMetrics: ProaComparisonItem[] = metrics.map(metric => {
    const hierarchyInfo = DRE_LINE_HIERARCHY[metric.id]
    if (!hierarchyInfo) {
      return { ...metric, parentId: null, isGrouper: false, level: 0 }
    }

    // Calculate level by traversing parents
    let level = 0
    let currentParentId: string | null = hierarchyInfo.parentId
    while (currentParentId) {
      level++
      const parentInfo = DRE_LINE_HIERARCHY[currentParentId]
      currentParentId = parentInfo?.parentId ?? null
    }

    return {
      ...metric,
      name: LINE_ITEM_NAMES[metric.id] || metric.name,
      parentId: hierarchyInfo.parentId ?? null,
      isGrouper: hierarchyInfo.isGrouper,
      level,
    }
  })

  // Combine grouper rows and metrics
  const allItems = [...grouperRows, ...enrichedMetrics]

  // Add derived stretch target rows for Lucro Líquido (120% and 150%)
  // Only create if there's an explicit PROA set for LucroLiquido (proaAnnual !== 0)
  const lucroLiquido = enrichedMetrics.find(m => m.id === 'LucroLiquido')
  if (lucroLiquido && lucroLiquido.proaAnnual !== 0) {
    // 120% stretch target
    const proa120Annual = lucroLiquido.proaAnnual * 1.2
    const proa120YTD = lucroLiquido.proaYTD * 1.2
    const proa120Month = lucroLiquido.proaMonth * 1.2
    allItems.push({
      id: 'LucroLiquido120',
      name: 'Lucro Líquido 120%',
      entryMode: 'direct' as EntryMode,
      proaAnnual: proa120Annual,
      proaYTD: proa120YTD,
      proaMonth: proa120Month,
      ajuste: 0,
      realAnnual: lucroLiquido.realAnnual,
      realYTD: lucroLiquido.realYTD,
      realMonth: lucroLiquido.realMonth,
      varianceAnnual: lucroLiquido.realAnnual !== null ? lucroLiquido.realAnnual - proa120Annual : null,
      varianceYTD: lucroLiquido.realYTD !== null ? lucroLiquido.realYTD - proa120YTD : null,
      varianceMonth: lucroLiquido.realMonth !== null ? lucroLiquido.realMonth - proa120Month : null,
      achievementPercent: lucroLiquido.realYTD !== null && proa120YTD !== 0 ? (lucroLiquido.realYTD / proa120YTD) * 100 : null,
      parentId: null,
      isGrouper: false,
      level: 0,
    })

    // 150% stretch target
    const proa150Annual = lucroLiquido.proaAnnual * 1.5
    const proa150YTD = lucroLiquido.proaYTD * 1.5
    const proa150Month = lucroLiquido.proaMonth * 1.5
    allItems.push({
      id: 'LucroLiquido150',
      name: 'Lucro Líquido 150%',
      entryMode: 'direct' as EntryMode,
      proaAnnual: proa150Annual,
      proaYTD: proa150YTD,
      proaMonth: proa150Month,
      ajuste: 0,
      realAnnual: lucroLiquido.realAnnual,
      realYTD: lucroLiquido.realYTD,
      realMonth: lucroLiquido.realMonth,
      varianceAnnual: lucroLiquido.realAnnual !== null ? lucroLiquido.realAnnual - proa150Annual : null,
      varianceYTD: lucroLiquido.realYTD !== null ? lucroLiquido.realYTD - proa150YTD : null,
      varianceMonth: lucroLiquido.realMonth !== null ? lucroLiquido.realMonth - proa150Month : null,
      achievementPercent: lucroLiquido.realYTD !== null && proa150YTD !== 0 ? (lucroLiquido.realYTD / proa150YTD) * 100 : null,
      parentId: null,
      isGrouper: false,
      level: 0,
    })
  }

  // Sort by hierarchy order
  allItems.sort((a, b) => {
    const orderA = DRE_LINE_HIERARCHY[a.id]?.order ?? 999
    const orderB = DRE_LINE_HIERARCHY[b.id]?.order ?? 999
    return orderA - orderB
  })

  return allItems
}

export interface ProaMonthlyComparison {
  period: string
  label: string
  proa: number
  real: number | null
  variance: number | null
  achievementPercent: number | null
}

export interface ProaComparisonOutput {
  scenario: ProaScenario
  metrics: ProaComparisonItem[]
  totals: {
    proaReceita: number
    proaDespesas: number
    proaLucro: number
    realReceita: number | null
    realDespesas: number | null
    realLucro: number | null
    varianceReceita: number | null
    varianceDespesas: number | null
    varianceLucro: number | null
  }
  monthlyComparison: ProaMonthlyComparison[]
}

export interface OfficialProaResponse {
  scenario: ProaScenario | null
  metrics: MetricProa[]
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const proaKeys = {
  all: ['proa'] as const,
  scenarios: (year: number) => [...proaKeys.all, 'scenarios', year] as const,
  scenario: (id: string) => [...proaKeys.all, 'scenario', id] as const,
  officialProa: (year: number) => [...proaKeys.all, 'officialProa', year] as const,
  metricProas: (scenarioId: string, year: number) => [...proaKeys.all, 'metricProas', scenarioId, year] as const,
  metricProa: (scenarioId: string, year: number, lineItem: string) =>
    [...proaKeys.all, 'metricProa', scenarioId, year, lineItem] as const,
  comparison: (scenarioId: string, year: number) => [...proaKeys.all, 'comparison', scenarioId, year] as const,
  monthlyComparison: (scenarioId: string, year: number, lineItem?: string, dimensionId?: string) =>
    [...proaKeys.all, 'monthlyComparison', scenarioId, year, lineItem, dimensionId] as const,
}

// ============================================================================
// API HELPERS
// ============================================================================

const PROA_API_BASE = '/monumenta-trpc/proa'

async function proaGet<T>(path: string, params?: Record<string, string | number>): Promise<T> {
  const queryString = params
    ? '?input=' + encodeURIComponent(JSON.stringify(params))
    : ''

  const fullPath = `${PROA_API_BASE}${path}${queryString}`

  const restOperation = get({
    apiName: 'monumenta-trpc',
    path: fullPath,
    options: {
      headers: createImpersonatedApiHeaders(),
    },
  })

  const response = await restOperation.response
  const data = await response.body.json()

  const result = data as { result?: { data?: T } }
  return result?.result?.data ?? (data as T)
}

async function proaPost<T, I>(path: string, input: I): Promise<T> {
  const fullPath = `${PROA_API_BASE}${path}`

  const restOperation = post({
    apiName: 'monumenta-trpc',
    path: fullPath,
    options: {
      headers: createImpersonatedApiHeaders(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      body: input as any,
    },
  })

  const response = await restOperation.response
  const data = await response.body.json()

  const result = data as { result?: { data?: T } }
  return result?.result?.data ?? (data as T)
}

// ============================================================================
// SCENARIO HOOKS
// ============================================================================

export function useListScenarios(year: number) {
  return useQuery({
    queryKey: proaKeys.scenarios(year),
    queryFn: () => proaGet<ProaScenario[]>('/listScenarios', { year }),
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetScenario(scenarioId: string | null) {
  return useQuery({
    queryKey: proaKeys.scenario(scenarioId ?? ''),
    queryFn: () => proaGet<ProaScenario | null>('/getScenario', { scenarioId: scenarioId! }),
    enabled: !!scenarioId,
    staleTime: 1000 * 60 * 5,
  })
}

export function useCreateScenario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { name: string; description?: string; year: number; baseScenarioId?: string }) =>
      proaPost<ProaScenario, typeof input>('/createScenario', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: proaKeys.scenarios(data.year) })
    },
  })
}

export function useUpdateScenario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { scenarioId: string; name?: string; description?: string; isLocked?: boolean }) =>
      proaPost<ProaScenario, typeof input>('/updateScenario', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: proaKeys.scenario(data.scenarioId) })
      queryClient.invalidateQueries({ queryKey: proaKeys.scenarios(data.year) })
    },
  })
}

export function useDeleteScenario() {
  const queryClient = useQueryClient()
  const { selectedScenarioId, setSelectedScenarioId } = useProaStore()

  return useMutation({
    mutationFn: (input: { scenarioId: string; year: number }) =>
      proaPost<{ success: boolean }, { scenarioId: string }>('/deleteScenario', { scenarioId: input.scenarioId }),
    onSuccess: (_, variables) => {
      if (selectedScenarioId === variables.scenarioId) {
        setSelectedScenarioId(null)
      }
      queryClient.invalidateQueries({ queryKey: proaKeys.scenarios(variables.year) })
    },
  })
}

export function useCloneScenario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { sourceScenarioId: string; newName: string; newYear: number }) =>
      proaPost<ProaScenario, typeof input>('/cloneScenario', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: proaKeys.scenarios(data.year) })
    },
  })
}

export function useSetOfficialScenario() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: { scenarioId: string }) =>
      proaPost<ProaScenario, typeof input>('/setOfficialScenario', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: proaKeys.scenarios(data.year) })
      queryClient.invalidateQueries({ queryKey: proaKeys.officialProa(data.year) })
      queryClient.invalidateQueries({ queryKey: proaKeys.scenario(data.scenarioId) })
    },
  })
}

export function useGetOfficialProa(year: number) {
  return useQuery({
    queryKey: proaKeys.officialProa(year),
    queryFn: () => proaGet<OfficialProaResponse>('/getOfficialProa', { year }),
    staleTime: 1000 * 60 * 5,
  })
}

// ============================================================================
// METRIC PROA HOOKS
// ============================================================================

export function useGetMetricProas(scenarioId: string | null, year: number) {
  return useQuery({
    queryKey: proaKeys.metricProas(scenarioId ?? '', year),
    queryFn: () => proaGet<MetricProa[]>('/getMetricProas', { scenarioId: scenarioId!, year }),
    enabled: !!scenarioId,
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetMetricProa(scenarioId: string | null, year: number, lineItem: string) {
  return useQuery({
    queryKey: proaKeys.metricProa(scenarioId ?? '', year, lineItem),
    queryFn: () => proaGet<MetricProa | null>('/getMetricProa', { scenarioId: scenarioId!, year, lineItem }),
    enabled: !!scenarioId && !!lineItem,
    staleTime: 1000 * 60 * 5,
  })
}

export function useUpsertMetricProaDirect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: {
      scenarioId: string
      year: number
      lineItem: string
      annual: number
      distribution?: { type: MonthlyDistributionType }
      notes?: string
    }) => proaPost<MetricProa, typeof input>('/upsertMetricProaDirect', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: proaKeys.metricProas(data.scenarioId, data.year) })
      queryClient.invalidateQueries({ queryKey: proaKeys.metricProa(data.scenarioId, data.year, data.lineItem) })
      queryClient.invalidateQueries({ queryKey: proaKeys.comparison(data.scenarioId, data.year) })
    },
  })
}

export function useBulkUpsertMetricProas() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: {
      scenarioId: string
      year: number
      items: Array<{
        lineItem: string
        annual: number
        distribution?: { type: MonthlyDistributionType }
        notes?: string
      }>
    }) => proaPost<MetricProa[], typeof input>('/bulkUpsertMetricProas', input),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: proaKeys.metricProas(variables.scenarioId, variables.year) })
      queryClient.invalidateQueries({ queryKey: proaKeys.comparison(variables.scenarioId, variables.year) })
    },
  })
}

export function useUpsertMetricProaBreakdown() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: {
      scenarioId: string
      year: number
      lineItem: string
      breakdowns: Array<{
        dimensionId: string
        dimensionLabel: string
        annual: number
        distribution?: { type: MonthlyDistributionType }
        notes?: string
      }>
      ajuste?: number
      distribution?: { type: MonthlyDistributionType }
      notes?: string
    }) => proaPost<MetricProa, typeof input>('/upsertMetricProaBreakdown', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: proaKeys.metricProas(data.scenarioId, data.year) })
      queryClient.invalidateQueries({ queryKey: proaKeys.metricProa(data.scenarioId, data.year, data.lineItem) })
      queryClient.invalidateQueries({ queryKey: proaKeys.comparison(data.scenarioId, data.year) })
    },
  })
}

export function useUpsertBreakdown() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: {
      scenarioId: string
      year: number
      lineItem: string
      dimensionId: string
      dimensionLabel: string
      annual: number
      distribution?: { type: MonthlyDistributionType }
      notes?: string
    }) => proaPost<MetricProa, typeof input>('/upsertBreakdown', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: proaKeys.metricProas(data.scenarioId, data.year) })
      queryClient.invalidateQueries({ queryKey: proaKeys.metricProa(data.scenarioId, data.year, data.lineItem) })
      queryClient.invalidateQueries({ queryKey: proaKeys.comparison(data.scenarioId, data.year) })
    },
  })
}

export function useRemoveBreakdown() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: {
      scenarioId: string
      year: number
      lineItem: string
      dimensionId: string
    }) => proaPost<MetricProa, typeof input>('/removeBreakdown', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: proaKeys.metricProas(data.scenarioId, data.year) })
      queryClient.invalidateQueries({ queryKey: proaKeys.metricProa(data.scenarioId, data.year, data.lineItem) })
      queryClient.invalidateQueries({ queryKey: proaKeys.comparison(data.scenarioId, data.year) })
    },
  })
}

// ============================================================================
// COMPARISON HOOKS
// ============================================================================

export function useGetComparison(scenarioId: string | null, year: number) {
  return useQuery({
    queryKey: proaKeys.comparison(scenarioId ?? '', year),
    queryFn: () => proaGet<ProaComparisonOutput>('/getComparison', { scenarioId: scenarioId!, year }),
    enabled: !!scenarioId,
    staleTime: 1000 * 60 * 5,
  })
}

export function useGetMonthlyComparison(
  scenarioId: string | null,
  year: number,
  options?: { lineItem?: string; dimensionId?: string }
) {
  return useQuery({
    queryKey: proaKeys.monthlyComparison(scenarioId ?? '', year, options?.lineItem, options?.dimensionId),
    queryFn: () =>
      proaGet<ProaMonthlyComparison[]>('/getMonthlyComparison', {
        scenarioId: scenarioId!,
        year,
        ...(options?.lineItem && { lineItem: options.lineItem }),
        ...(options?.dimensionId && { dimensionId: options.dimensionId }),
      }),
    enabled: !!scenarioId,
    staleTime: 1000 * 60 * 5,
  })
}

// ============================================================================
// COMBINED HOOKS (using store)
// ============================================================================

export function useSelectedScenarioComparison() {
  const { selectedScenarioId, selectedYear } = useProaStore()
  return useGetComparison(selectedScenarioId, selectedYear)
}

export function useSelectedScenario() {
  const { selectedScenarioId } = useProaStore()
  return useGetScenario(selectedScenarioId)
}

// ============================================================================
// ENHANCED COMPARISON WITH REAL DRE DATA
// ============================================================================

const DRE_LINE_TO_CACHE_FIELD: Record<string, string> = {
  FaturamentoTotal: 'FaturamentoTotal',
  Repasse: 'Repasse',
  ReceitaFluxoGerencial: 'ReceitaFluxoGerencial',
  ReceitaBruta: 'ReceitaBruta',
  ISS: 'ISS',
  PIS: 'PIS',
  COFINS: 'COFINS',
  ReceitaLiquida: 'ReceitaLiquida',
  ServicosPrestados: 'ServicosPrestados',
  Timesheet: 'Timesheet',
  Bonificacao: 'Bonificacao',
  Folha: 'Folha',
  Freelancer: 'Freelancer',
  FreelancerFixo: 'FreelancerFixo',
  FreelancerEmpresa: 'FreelancerEmpresa',
  MaoDeObra: 'MaoDeObra',
  LucroBruto: 'LucroBruto',
  DespesasOperacionais: 'DespesasOperacionais',
  ReceitasFinanceiras: 'ReceitasFinanceiras',
  DespesasFinanceiras: 'DespesasFinanceiras',
  LucroOperacional: 'LucroOperacional',
  ContribuicaoSocial: 'ContribuicaoSocial',
  IRPJ: 'IRPJ',
  LucroLiquido: 'LucroLiquido',
}

function calculateVariance(proa: number, real: number | null): number | null {
  if (real === null) return null
  return real - proa
}

function calculateAchievement(proa: number, real: number | null): number | null {
  if (real === null || proa === 0) return null
  return (real / proa) * 100
}

export function useSelectedScenarioComparisonWithRealData() {
  const { selectedScenarioId, selectedYear } = useProaStore()

  const proaQuery = useGetComparison(selectedScenarioId, selectedYear)

  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1

  const startDate = new Date(selectedYear, 0, 1)
  const endDate = selectedYear === currentYear
    ? new Date(selectedYear, currentMonth - 1 + 1, 0)
    : new Date(selectedYear, 11, 31)

  // Fetch DRE report for totals
  const dreQuery = trpc.dre.getReport.useQuery(
    {
      dataInicial: startDate,
      dataFinal: endDate,
    },
    {
      enabled: !!selectedScenarioId && !!selectedYear,
      staleTime: 1000 * 60 * 5,
    }
  )

  // Fetch client matrix for client-level breakdown data
  const clientMatrixQuery = trpc.dre.getClientMatrix.useQuery(
    {
      dataInicial: startDate,
      dataFinal: endDate,
    },
    {
      enabled: !!selectedScenarioId && !!selectedYear,
      staleTime: 1000 * 60 * 5,
    }
  )

  const mergedData = useMemo(() => {
    if (!proaQuery.data) return null

    const dreData = dreQuery.data || []
    const clientMatrixData = clientMatrixQuery.data
    const scenario = proaQuery.data.scenario
    const proaMetrics = proaQuery.data.metrics

    const ytdMonth = selectedYear === currentYear ? currentMonth : 12

    // Build DRE totals by line item
    const dreAnnualTotals: Record<string, number> = {}
    const dreYTDTotals: Record<string, number> = {}
    const dreMonthTotals: Record<string, number> = {}

    dreData.forEach((row) => {
      const rowMonth = Number(row.Mes)

      Object.keys(DRE_LINE_TO_CACHE_FIELD).forEach((lineItem) => {
        const fieldName = DRE_LINE_TO_CACHE_FIELD[lineItem]
        const value = Number((row as Record<string, unknown>)[fieldName]) || 0

        dreAnnualTotals[lineItem] = (dreAnnualTotals[lineItem] || 0) + value

        if (rowMonth <= ytdMonth) {
          dreYTDTotals[lineItem] = (dreYTDTotals[lineItem] || 0) + value
        }

        if (rowMonth === ytdMonth) {
          dreMonthTotals[lineItem] = (dreMonthTotals[lineItem] || 0) + value
        }
      })
    })

    // Build client-level real data from client matrix
    // Maps: clientCode -> { receita, custos, ... }
    const clientRealData = new Map<string, {
      receita: number
      custos: number
      receitaYTD: number
      custosYTD: number
      receitaMonth: number
      custosMonth: number
    }>()

    if (clientMatrixData?.clients) {
      // Client matrix returns aggregated data for the period
      // For YTD/Month breakdowns, we use monthlyByClient if available
      clientMatrixData.clients.forEach((client) => {
        const codigo = client.codigoCliente ?? ''
        clientRealData.set(codigo, {
          receita: client.receita ?? 0,
          custos: client.custos ?? 0,
          receitaYTD: client.receita ?? 0, // For now, use total as YTD (period already filtered)
          custosYTD: client.custos ?? 0,
          receitaMonth: 0, // Will be populated from monthlyByClient
          custosMonth: 0,
        })
      })

      // If monthlyByClient is available, calculate monthly values
      if (clientMatrixData.monthlyByClient) {
        Object.entries(clientMatrixData.monthlyByClient).forEach(([clientCode, monthlyData]) => {
          const existing = clientRealData.get(clientCode)
          if (existing && Array.isArray(monthlyData)) {
            // Find the current month's data
            const currentMonthData = monthlyData.find((m) => {
              const period = (m as { period?: string }).period
              if (!period) return false
              const monthNum = parseInt(period.split('-')[1], 10)
              return monthNum === ytdMonth
            }) as { receita?: number; custos?: number } | undefined

            if (currentMonthData) {
              existing.receitaMonth = currentMonthData.receita ?? 0
              existing.custosMonth = currentMonthData.custos ?? 0
            }
          }
        })
      }
    }

    // First, find the original LucroLiquido metric to check if it has an explicit PROA
    // We need to check this before enrichment to avoid using auto-calculated values
    const originalLucroLiquido = proaMetrics.find(m => m.id === 'LucroLiquido')
    // If LucroLiquido proaAnnual is 0, it means no explicit PROA was set - don't use auto-calculated values
    const hasExplicitLucroLiquidoProa = originalLucroLiquido && originalLucroLiquido.proaAnnual !== 0

    // Enrich metrics with real data, including breakdowns
    const enrichedMetrics: ProaComparisonItem[] = proaMetrics.map((proa) => {
      const realAnnual = dreAnnualTotals[proa.id] ?? null
      const realYTD = dreYTDTotals[proa.id] ?? null
      const realMonth = dreMonthTotals[proa.id] ?? null

      // For LucroLiquido without explicit PROA, zero out the PROA values
      // This prevents showing auto-calculated values when user hasn't set a target
      const isLucroLiquidoWithoutProa = proa.id === 'LucroLiquido' && !hasExplicitLucroLiquidoProa
      const effectiveProaAnnual = isLucroLiquidoWithoutProa ? 0 : proa.proaAnnual
      const effectiveProaYTD = isLucroLiquidoWithoutProa ? 0 : proa.proaYTD
      const effectiveProaMonth = isLucroLiquidoWithoutProa ? 0 : proa.proaMonth

      // Enrich breakdowns with real client data
      let enrichedBreakdowns: BreakdownComparisonItem[] | undefined
      if (proa.breakdowns && proa.breakdowns.length > 0) {
        enrichedBreakdowns = proa.breakdowns.map((breakdown) => {
          const clientReal = clientRealData.get(breakdown.dimensionId)

          // Map line item to the appropriate client metric
          // ReceitaBruta -> receita, ServicosPrestados/Timesheet -> custos, etc.
          let realAnnualClient: number | null = null
          let realYTDClient: number | null = null
          let realMonthClient: number | null = null

          if (clientReal) {
            // Determine which field to use based on line item
            if (proa.id === 'ReceitaBruta' || proa.id === 'Repasse') {
              realAnnualClient = clientReal.receita
              realYTDClient = clientReal.receitaYTD
              realMonthClient = clientReal.receitaMonth
            } else if (proa.id === 'ServicosPrestados' || proa.id === 'Timesheet') {
              realAnnualClient = clientReal.custos
              realYTDClient = clientReal.custosYTD
              realMonthClient = clientReal.custosMonth
            }
          }

          return {
            ...breakdown,
            realAnnual: realAnnualClient,
            realYTD: realYTDClient,
            realMonth: realMonthClient,
            varianceAnnual: calculateVariance(breakdown.proaAnnual, realAnnualClient),
            varianceYTD: calculateVariance(breakdown.proaYTD, realYTDClient),
            varianceMonth: calculateVariance(breakdown.proaMonth, realMonthClient),
            achievementPercent: calculateAchievement(breakdown.proaYTD, realYTDClient),
          }
        })
      }

      return {
        ...proa,
        proaAnnual: effectiveProaAnnual,
        proaYTD: effectiveProaYTD,
        proaMonth: effectiveProaMonth,
        realAnnual,
        realYTD,
        realMonth,
        varianceAnnual: isLucroLiquidoWithoutProa ? null : calculateVariance(effectiveProaAnnual, realAnnual),
        varianceYTD: isLucroLiquidoWithoutProa ? null : calculateVariance(effectiveProaYTD, realYTD),
        varianceMonth: isLucroLiquidoWithoutProa ? null : calculateVariance(effectiveProaMonth, realMonth),
        achievementPercent: isLucroLiquidoWithoutProa ? null : calculateAchievement(effectiveProaYTD, realYTD),
        breakdowns: enrichedBreakdowns,
      }
    })

    const realReceita = dreAnnualTotals['ReceitaBruta'] ?? null
    const realDespesas = (
      (dreAnnualTotals['ServicosPrestados'] || 0) +
      (dreAnnualTotals['Timesheet'] || 0) +
      (dreAnnualTotals['Folha'] || 0) +
      (dreAnnualTotals['MaoDeObra'] || 0) +
      (dreAnnualTotals['Bonificacao'] || 0) +
      (dreAnnualTotals['DespesasOperacionais'] || 0)
    ) || null
    const realLucro = dreAnnualTotals['LucroLiquido'] ?? null

    const enrichedTotals = {
      ...proaQuery.data.totals,
      // Only use proaLucro if there's an explicit PROA set for LucroLiquido
      proaLucro: hasExplicitLucroLiquidoProa ? proaQuery.data.totals.proaLucro : 0,
      realReceita,
      realDespesas,
      realLucro,
      varianceReceita: calculateVariance(proaQuery.data.totals.proaReceita, realReceita),
      varianceDespesas: calculateVariance(proaQuery.data.totals.proaDespesas, realDespesas),
      varianceLucro: hasExplicitLucroLiquidoProa ? calculateVariance(proaQuery.data.totals.proaLucro, realLucro) : null,
    }

    const monthlyByMonth = new Map<number, number>()
    dreData.forEach((row) => {
      const receitaBruta = Number(row.ReceitaBruta) || 0
      monthlyByMonth.set(Number(row.Mes), receitaBruta)
    })

    const enrichedMonthlyComparison: ProaMonthlyComparison[] = proaQuery.data.monthlyComparison.map((m, idx) => {
      const month = idx + 1
      const real = monthlyByMonth.get(month) ?? null
      return {
        ...m,
        real,
        variance: calculateVariance(m.proa, real),
        achievementPercent: calculateAchievement(m.proa, real),
      }
    })

    return {
      scenario,
      metrics: enrichedMetrics,
      totals: enrichedTotals,
      monthlyComparison: enrichedMonthlyComparison,
    } as ProaComparisonOutput
  }, [proaQuery.data, dreQuery.data, clientMatrixQuery.data, selectedYear, currentYear, currentMonth])

  return {
    data: mergedData,
    isLoading: proaQuery.isLoading || dreQuery.isLoading || clientMatrixQuery.isLoading,
    error: proaQuery.error || dreQuery.error || clientMatrixQuery.error,
    refetch: () => {
      proaQuery.refetch()
      dreQuery.refetch()
      clientMatrixQuery.refetch()
    },
  }
}

